import type { Session } from '@supabase/supabase-js';
import type { ZodType } from 'zod';

// Minimal authenticated fetch wrapper for the backoffice, mirroring the
// dashboard's (apps/dashboard/src/lib/api.ts). Throws a typed `ApiError` so
// callers can branch on `code` / `status` without re-parsing the body, and
// Zod-parses every response (the CLAUDE.md non-negotiable that all external
// data is validated before use).

interface ErrorResponseBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Resolved lazily so importing this module never depends on VITE_API_URL being
// defined — a missing var only matters when a request is actually fired.
const getBaseUrl = (): string => {
  const url = import.meta.env.VITE_API_URL;
  if (typeof url !== 'string') {
    throw new Error('VITE_API_URL is not configured');
  }
  return url.replace(/\/$/, '');
};

export interface ApiFetchOptions<T = unknown> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  // Required for every authenticated endpoint — the Supabase access token from
  // the current session.
  session: Session;
  signal?: AbortSignal;
  // When provided, the JSON body is Zod-parsed (not cast) before returning.
  schema?: ZodType<T>;
}

export const apiFetch = async <T>(path: string, options: ApiFetchOptions<T>): Promise<T> => {
  const { method = 'GET', body, session, signal, schema } = options;

  const headers: Record<string, string> = {
    authorization: `Bearer ${session.access_token}`,
  };
  const init: RequestInit = {
    method,
    headers,
    signal: signal ?? null,
  };
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const response = await fetch(`${getBaseUrl()}${path}`, init);

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as ErrorResponseBody | null;
    const code = errorBody?.error?.code ?? 'UNKNOWN_ERROR';
    const message = errorBody?.error?.message ?? response.statusText ?? 'Request failed';
    throw new ApiError(response.status, code, message, errorBody?.error?.details);
  }

  if (response.status === 204) {
    return null as T;
  }
  const json: unknown = await response.json();
  return schema ? schema.parse(json) : (json as T);
};
